import { Injectable } from '@angular/core';
import { User, UserType } from 'app/auth/models/User';
import { OnyxEvent } from 'app/event/models/event';
import { EventStore } from 'app/event/service/event.store';
import { EventUtils } from 'app/event/utils/event-utils';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { MultiAgency } from './../models/multi-agency';

@Injectable()
export class MultiAgencyService {
  private static readonly CURRENT_AGENCY = 'CURRENT_AGENCY';
  private static readonly MULTI_AGENCY = 'MULTI_AGENCY';

  private multiAgency = List<MultiAgency>();

  private singleAgencySubject: ReplaySubject<boolean> = new ReplaySubject(1);
  private multiAgencySubject: ReplaySubject<boolean> = new ReplaySubject(1);

  private defaultAgency: BehaviorSubject<MultiAgency> = new BehaviorSubject<MultiAgency>(null);
  defaultAgency$: Observable<MultiAgency> = this.defaultAgency.asObservable();

  private agenciesList: BehaviorSubject<List<MultiAgency>> = new BehaviorSubject<List<MultiAgency>>(null);
  agenciesList$: Observable<List<MultiAgency>> = this.agenciesList.asObservable();

  private isEventSelected: BehaviorSubject<boolean> = new BehaviorSubject(null);
  isEventSelected$: Observable<boolean> = this.isEventSelected.asObservable();

  private agencySelectedUI: boolean;

  user: User;

  private currentAgencyId: string = null;

  onyxEvent: OnyxEvent;

  constructor(
    private authenticationStore: AuthenticationStore,
    private userLocalStorageService: UserLocalStorageService,
    private eventStore: EventStore
  ) {
    this.init();
  }

  protected init(): void {
    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.authenticationStore.defaultAgency$.subscribe(() => {
      this.setAgencyInfo();
      this.setMultiAgencies();
    });

    this.eventStore.selectedEvent.subscribe((event: OnyxEvent) => {
      if (event && EventUtils.getEventName(event, this.authenticationStore.isAgencyUser())) {
        this.isEventSelected.next(true);
      } else {
        this.isEventSelected.next(false);
      }

      this.onyxEvent = new OnyxEvent(event);
    });

    this.agencySelectedUI = false;

  }

  private setAgencyInfo(): void {
    if (!this.agencySelectedUI && this.user.type === UserType.AGENCY) {
      const current_agency_key = this.user.username.toUpperCase() + '_' + MultiAgencyService.CURRENT_AGENCY;
      let agency: MultiAgency = this.userLocalStorageService.getItemAsJson(current_agency_key) as MultiAgency;
      if (agency) {
        this.setDefaultAgency(agency);
      } else {
        this.resetDefaultAgency();
      }
    }
  }

  public setDefaultAgency(multiAgency: MultiAgency) {
    if (this.currentAgencyId !== multiAgency.agencyId) {
      const current_agency_key = this.user.username.toUpperCase() + '_' + MultiAgencyService.CURRENT_AGENCY;
      this.userLocalStorageService.setItem(current_agency_key, JSON.stringify(multiAgency));
      this.currentAgencyId = multiAgency.agencyId;
      this.defaultAgency.next(multiAgency);
    }
  }

  private resetDefaultAgency(): void {
    this.defaultAgency.next(null);
  }

  get oldAgencyId(): string {
    return this.currentAgencyId;
  }

  protected setMultiAgencies() {
    if (this.userLocalStorageService.getItem(MultiAgencyService.MULTI_AGENCY)) {
      this.multiAgency = this.userLocalStorageService.getItemAsJson(MultiAgencyService.MULTI_AGENCY) as List<MultiAgency>;
      this.agenciesList.next(this.multiAgency);
    } else {
      this.multiAgency = null;
      this.agenciesList.next(null);
    }
  }

  get agencies(): Observable<List<MultiAgency>> {
    return this.agenciesList$
      .catch((error: any) => {
        return Observable.throw(error);
      })
      .map((agencies: List<MultiAgency>) => {
        return agencies;
      });
  }

  get defaultAgencyId(): Observable<string> {
    return this.defaultAgency$
      .catch((error: any) => {
        return Observable.throw(error);
      })
      .map((agency: MultiAgency) => {
        return agency ? agency.agencyId : null;
      });
  }

  get defaultAgencyName(): Observable<string> {
    return this.defaultAgency$
      .catch((error: any) => {
        return Observable.throw(error);
      })
      .map((agency: MultiAgency) => {
        return agency.agencyName;
      });
  }

  public isMultiAgency():  Observable<boolean> {
    this.multiAgencySubject.next(this.hasDefaultAgency() && this.hasMultiAgency());

    return this.multiAgencySubject.asObservable();
  }

  public isSingleAgency(): Observable<boolean> {
    this.singleAgencySubject.next(this.hasDefaultAgency() && !this.hasMultiAgency());

    return this.singleAgencySubject.asObservable();
  }

  public isSingleAgencyNew(): boolean {
    return this.defaultAgency$ && !this.hasMultiAgency();
  }

  private hasDefaultAgency(): boolean {
    let hasAgency: boolean;
    this.defaultAgency$.take(1).subscribe(agency => {
      if (agency && agency.agencyId) {
        hasAgency = true;
      } else {
        hasAgency = false;
      }
    });
    return hasAgency;
  }

  private hasMultiAgency(): boolean {
    return this.multiAgency && this.multiAgency[0];
  }

  public setEventSelected(value: boolean) {
    this.isEventSelected.next(value);
  }

  public setAgencySelectedUI(value: boolean): void {
    this.agencySelectedUI = value;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/shared/multi-agency/multi-agency.service.ts